// src/pages/Login/LoginPage.jsx
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowRight,
  FaCheck,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaTrophy,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import { useUser } from "./../../contexts/UserContext";
import { loginUser } from "./../../services/userServices";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";

const INPUT =
  "w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/80 dark:text-slate-100 dark:focus:border-indigo-500 dark:focus:ring-indigo-950";

export default function LoginPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useUser();

  const initialEmail = location.state?.email || "";

  const [form, setForm] = useState({
    email: initialEmail,
    password: "",
  });

  const [showPwd, setShowPwd] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const registeredMessage = Boolean(location.state?.registered);

  const isFormValid = useMemo(() => {
    return form.email.trim().includes("@") && form.password.trim().length > 0;
  }, [form]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormValid) {
      setErrorMessage("Merci de renseigner votre email et votre mot de passe.");
      return;
    }

    try {
      setLoading(true);
      setErrorMessage("");

      const cleanEmail = form.email.trim().toLowerCase();
      const data = await loginUser(cleanEmail, form.password);

      if (!data?.userInfo || !data?.token) {
        setErrorMessage("Email ou mot de passe incorrect. Veuillez réessayer.");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("userInfo", JSON.stringify(data.userInfo));

      updateUser(data.userInfo);
      navigate("/");
    } catch (error) {
      console.error("Erreur connexion :", error);
      setErrorMessage(
        "Impossible de se connecter pour le moment. Veuillez réessayer plus tard."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="relative min-h-screen overflow-hidden bg-slate-950 bg-cover bg-center px-3 py-8 pt-[120px] text-slate-900 dark:text-white sm:px-5 md:pt-[145px]"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <ScrollIndicator />

      <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-[2px]" />
      <div className="absolute -right-24 top-20 h-72 w-72 rounded-full bg-indigo-500/30 blur-3xl" />
      <div className="absolute bottom-0 left-10 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[1180px] gap-6 lg:grid-cols-[minmax(0,1fr)_460px] lg:items-center">
        <section className="hidden text-white lg:block">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-white/80 backdrop-blur">
            <FaTrophy />
            MonAppliTournoi
          </div>

          <h1 className="mt-5 max-w-3xl text-5xl font-bold tracking-tight">
            Connectez-vous à votre espace compétition.
          </h1>

          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">
            Retrouvez vos compétitions, gérez les inscriptions, les pesées, les
            poules, les tapis, les combats, les résultats publics et les QR Codes
            depuis votre tableau de bord.
          </p>

          <div className="mt-8 grid max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
            <HeroFeature
              icon={FaShieldAlt}
              title="Accès sécurisé"
              text="Connexion protégée par votre compte."
            />
            <HeroFeature
              icon={FaUsers}
              title="Clubs invités"
              text="Suivi des compétiteurs et inscriptions."
            />
            <HeroFeature
              icon={FaTrophy}
              title="Résultats live"
              text="Gestion complète de la compétition."
            />
          </div>
        </section>

        <section className="mx-auto w-full max-w-xl lg:max-w-none">
          <div className="overflow-hidden rounded-[34px] border border-white/20 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-slate-800 dark:bg-slate-900/95">
            <div className="relative overflow-hidden bg-slate-950 p-6 text-white sm:p-8">
              <div className="absolute -right-14 -top-14 h-40 w-40 rounded-full bg-indigo-500/30 blur-3xl" />
              <div className="absolute -bottom-16 left-1/3 h-40 w-40 rounded-full bg-pink-500/20 blur-3xl" />

              <div className="relative">
                <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-xl">
                  <FaSignInAlt />
                </div>

                <h2 className="text-3xl font-bold tracking-tight">
                  Connexion
                </h2>

                <p className="mt-2 text-sm leading-relaxed text-slate-300">
                  Accédez à votre espace organisateur ou à votre compte de
                  démonstration.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5 sm:p-7">
              {registeredMessage ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
                  <div className="flex items-start gap-3">
                    <FaCheck className="mt-0.5 shrink-0" />
                    <span>
                      Votre compte a bien été créé. Vous pouvez maintenant vous
                      connecter.
                    </span>
                  </div>
                </div>
              ) : null}

              {errorMessage ? (
                <div
                  className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                  role="alert"
                >
                  {errorMessage}
                </div>
              ) : null}

              <Field label="Email" icon={FaEnvelope}>
                <input
                  type="email"
                  name="email"
                  placeholder="votre.email@exemple.fr"
                  className={INPUT}
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required
                />
              </Field>

              <Field label="Mot de passe" icon={FaLock}>
                <div className="relative">
                  <input
                    type={showPwd ? "text" : "password"}
                    name="password"
                    placeholder="Votre mot de passe"
                    className={`${INPUT} pr-12`}
                    value={form.password}
                    onChange={handleChange}
                    autoComplete="current-password"
                    required
                  />

                  <button
                    type="button"
                    onClick={() => setShowPwd((value) => !value)}
                    aria-label={
                      showPwd
                        ? "Masquer le mot de passe"
                        : "Afficher le mot de passe"
                    }
                    aria-pressed={showPwd}
                    title={showPwd ? "Masquer" : "Afficher"}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 transition hover:text-slate-800 dark:text-slate-300 dark:hover:text-white"
                  >
                    {showPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </Field>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Se souvenir de moi
                </label>

                <Link
                  to="/forgot-password"
                  className="text-sm font-bold text-indigo-600 transition hover:text-indigo-500 hover:underline dark:text-indigo-300 dark:hover:text-indigo-200"
                >
                  Mot de passe oublié ?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading || !isFormValid}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 via-violet-600 to-pink-500 px-4 py-3.5 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
              >
                <FaSignInAlt />
                {loading ? "Connexion en cours..." : "Se connecter"}
                {!loading ? (
                  <FaArrowRight className="transition group-hover:translate-x-1" />
                ) : null}
              </button>

              <div className="rounded-2xl bg-slate-50 p-4 text-center text-sm text-slate-600 dark:bg-slate-950/60 dark:text-slate-300">
                Pas encore de compte ?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="inline-flex items-center gap-1 font-bold text-indigo-600 hover:underline dark:text-indigo-300"
                >
                  <FaUserPlus className="text-xs" />
                  Créer un compte
                </button>
              </div>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <label className="block">
      <span className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700 dark:text-slate-200">
        <Icon className="text-indigo-500" />
        {label}
      </span>

      {children}
    </label>
  );
}

function HeroFeature({ icon: Icon, title, text }) {
  return (
    <div className="rounded-[24px] border border-white/10 bg-white/[0.08] p-4 backdrop-blur">
      <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
        <Icon />
      </div>

      <h3 className="font-bold text-white">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-300">{text}</p>
    </div>
  );
}